import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { Menu, X, ArrowLeft } from "lucide-react";

// Module-level flag: resets to false on every hard page refresh (JS reload),
// but persists across SPA route changes within the same session.
let offerDismissed = false;

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/case-studies", label: "Case Studies" },
  { href: "/contact", label: "Contact" },
];

interface WebsiteHeaderProps {
  showBackButton?: boolean;
}

export function WebsiteHeader({ showBackButton = false }: WebsiteHeaderProps) {
  const [location, navigate] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [offerVisible, setOfferVisible] = useState(() => !offerDismissed);

  const dismissOffer = () => {
    offerDismissed = true;
    setOfferVisible(false);
  };

  return (
    <header className="fixed w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
      {/* Announcement Bar */}
      {offerVisible && (
        <div className="relative w-full bg-blue-600 text-center py-2 px-10 text-xs sm:text-sm font-semibold text-white">
          <span className="font-extrabold uppercase tracking-widest mr-1">Limited Offer</span>
          — Get the <span className="font-bold">Single plan</span> for lifetime — <span className="font-bold">$79</span> once. Use code <span className="font-mono font-bold">LIFETIMESG</span>.{" "}
          <Link href="/register" className="underline font-bold hover:text-blue-200 transition-colors">Grab it now →</Link>
          <button
            onClick={dismissOffer}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
            aria-label="Dismiss offer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo + optional back button */}
          <div className="flex items-center gap-2">
            {showBackButton && (
              <button
                onClick={() => window.history.back()}
                className="md:hidden p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Link href="/">
              <SaaSGamaLogo size="lg" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6 lg:space-x-8">
            {navLinks.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                className={`font-medium transition-colors text-sm lg:text-base ${
                  location === href
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons + Hamburger */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="hidden md:inline-flex text-gray-700 hover:text-blue-600 hover:bg-blue-50 text-sm"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 md:px-5 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all text-sm"
              asChild
            >
              <Link href="/register">Get Started</Link>
            </Button>
            {/* Hamburger icon — mobile only */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="flex flex-col px-4 py-3 space-y-1">
            {navLinks.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                  location === href
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {label}
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
              >
                Sign In
              </Button>
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" asChild>
                <Link href="/register" onClick={() => setMobileMenuOpen(false)}>
                  Get Started Free
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
